import React, { useEffect } from 'react';
import { ExternalLink, Play } from 'lucide-react';

const ResourcesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  const tedTalks = [
    {
      title: "Keynote at Walsh State University",
      speaker: "Dr. Nicole Labor",
      views: "Featured",
      image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400&h=250&fit=crop",
      url: "https://www.youtube.com/watch?v=PY9DcIMGxMs"
    },
    {
      title: "Everything you think you know about addiction is wrong",
      speaker: "Johann Hari",
      views: "20M+",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=250&fit=crop",
      url: "https://www.youtube.com/watch?v=PY9DcIMGxMs"
    },
    {
      title: "Why do our brains get addicted?",
      speaker: "Nora Volkow",
      views: "1.5M+",
      image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400&h=250&fit=crop",
      url: "https://www.youtube.com/watch?v=Mnd2-al4LCU"
    },
    {
      title: "Addiction is a disease. We should treat it like one",
      speaker: "Michael Botticelli",
      views: "2M+",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=250&fit=crop",
      url: "https://www.youtube.com/watch?v=4Ew1M9TIK_k"
    },
    {
      title: "The Psychology of Self-Motivation",
      speaker: "Scott Geller",
      views: "8M+",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop",
      url: "https://www.youtube.com/watch?v=7sxpKhIbr0E"
    },
    {
      title: "How to make stress your friend",
      speaker: "Kelly McGonigal",
      views: "25M+",
      image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=250&fit=crop",
      url: "https://www.youtube.com/watch?v=RcGyVTAoXEU"
    }
  ];

  const stories = [
    {
      title: "Find Help & Treatment",
      tag: "Recovery Stories",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      url: "https://www.samhsa.gov/find-help/national-helpline"
    },
    {
      title: "Find AA Meetings Near You",
      tag: "Sober Community",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop",
      url: "https://www.aa.org/find-aa"
    },
    {
      title: "Al-Anon Family Support",
      tag: "Family Wellness",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=300&fit=crop",
      url: "https://al-anon.org/"
    }
  ];

  return (
    <main className="resources-page" data-testid="resources-page">
      <div className="resources-header">
        <h1>Recovery Resources</h1>
        <p>
          Curated collection of inspiring talks, educational content, and recovery 
          stories to support your journey.
        </p>
      </div>

      {/* Featured Section */}
      <section className="featured-section">
        <div className="featured-card" data-testid="featured-keynote">
          <span className="featured-badge">FEATURED</span>
          <h3>Keynote <span className="gold">Presentation</span></h3>
          
          <button 
            type="button"
            className="featured-video"
            onClick={() => window.open('https://www.youtube.com/watch?v=PY9DcIMGxMs', '_blank')}
          >
            <img 
              src="https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=900&h=400&fit=crop" 
              alt="Keynote presentation" 
            />
            <div className="play-btn">
              <Play size={28} fill="white" color="white" />
            </div>
          </button>

          <div className="featured-info">
            <h4>Keynote</h4>
            <h5>Dr. Nicole Labor</h5>
            <p>Keynote at Walsh State University</p>
            <p>
              Transformative insights on healing, trauma, and holistic wellness 
              from renowned speaker Dr. Nicole Labor.
            </p>
            <button 
              type="button"
              className="watch-link" 
              data-testid="watch-now-link"
              onClick={() => window.open('https://www.youtube.com/watch?v=PY9DcIMGxMs', '_blank')}
            >
              Watch Now <ExternalLink size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* TED Talks Section */}
      <section className="ted-section">
        <h2 className="section-title">Popular TED Talks on <span>Addiction & Recovery</span></h2>
        
        <div className="talks-grid">
          {tedTalks.map((talk, index) => (
            <button 
              key={index} 
              type="button"
              onClick={() => window.open(talk.url, '_blank')}
              className="talk-card"
              data-testid={`ted-talk-${index}`}
            >
              <div className="talk-thumbnail">
                <img src={talk.image} alt={talk.title} />
                <div className="play-btn">
                  <Play size={20} fill="white" color="white" />
                </div>
              </div>
              <div className="talk-info">
                <h4>{talk.title}</h4>
                <div className="talk-meta">
                  <span>{talk.speaker}</span>
                  <span>{talk.views} <ExternalLink size={12} /></span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Stories Section */}
      <section className="stories-section">
        <h2 className="section-title">More Inspiring <span>Resources</span></h2>
        <p className="section-subtitle">Recovery Stories & Community Content</p>
        
        <div className="stories-grid">
          {stories.map((story, index) => (
            <button 
              key={index} 
              type="button"
              onClick={() => window.open(story.url, '_blank')}
              className="story-card" 
              data-testid={`story-${index}`}
            >
              <div className="story-image">
                <img src={story.image} alt={story.title} />
                <span className="story-tag">{story.tag}</span>
              </div>
              <div className="story-info">
                <h4>{story.title}</h4>
                <span className="story-link">
                  <ExternalLink size={14} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ResourcesPage;
